import React, { useState } from 'react';
import { BaseModal } from '@/components/shared';
import type { MotivoExclusaoMovimentacao } from '../../../models';
import { MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS } from '../solicitacaoDetailsPage.definitions';

export interface ExcluirMovimentacaoConfirmacao {
  motivoExclusaoTipo: MotivoExclusaoMovimentacao;
  /** Obrigatório só quando `motivoExclusaoTipo === 'outro'`; opcional nos demais. */
  motivoExclusao: string | null;
}

interface ExcluirMovimentacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ex.: "Devolução — Notebook" — pra deixar claro qual Movimentação vai sair. */
  descricaoMovimentacao: string;
  onConfirmar: (dados: ExcluirMovimentacaoConfirmacao) => void;
  confirmando?: boolean;
}

/**
 * Exclusão (suave) de uma Movimentação — exclusiva do SI, ver Movimentacao.excluida.
 * Mesmo formato do ExcluirSolicitacaoModal: motivo num dropdown fechado + texto livre
 * (obrigatório só em "Outro motivo").
 */
export default function ExcluirMovimentacaoModal({
  isOpen,
  onClose,
  descricaoMovimentacao,
  onConfirmar,
  confirmando,
}: ExcluirMovimentacaoModalProps) {
  const [motivoTipo, setMotivoTipo] = useState<MotivoExclusaoMovimentacao | ''>('');
  const [observacao, setObservacao] = useState('');

  const handleClose = () => {
    setMotivoTipo('');
    setObservacao('');
    onClose();
  };

  const podeConfirmar =
    motivoTipo !== '' && (motivoTipo !== 'outro' || observacao.trim().length > 0) && !confirmando;

  const handleConfirmar = () => {
    if (motivoTipo === '' || !podeConfirmar) return;
    onConfirmar({ motivoExclusaoTipo: motivoTipo, motivoExclusao: observacao.trim() || null });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Excluir Movimentação"
      maxWidth="max-w-md"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Voltar
          </button>
          <button type="button" className="btn-primary" disabled={!podeConfirmar} onClick={handleConfirmar}>
            {confirmando ? 'Excluindo...' : 'Confirmar Exclusão'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 py-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{descricaoMovimentacao}</span> sai da Solicitação e deixa de
          bloquear a conclusão. Ela não é apagada — fica registrada, com o motivo, em "Movimentações excluídas".
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo *</label>
          <select
            className="input-primary mt-1 w-full"
            value={motivoTipo}
            onChange={(event) => setMotivoTipo(event.target.value as MotivoExclusaoMovimentacao)}
            autoFocus
          >
            <option value="" disabled>
              Selecione um motivo
            </option>
            {(Object.entries(MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS) as [MotivoExclusaoMovimentacao, string][]).map(
              ([valor, label]) => (
                <option key={valor} value={valor}>
                  {label}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {motivoTipo === 'outro' ? 'Descreva o motivo *' : 'Observação (opcional)'}
          </label>
          <textarea
            className="input-primary mt-1 w-full"
            rows={3}
            value={observacao}
            onChange={(event) => setObservacao(event.target.value)}
          />
        </div>
      </div>
    </BaseModal>
  );
}
